/**
 * TranscriptionEngine — live transcription via backend WebSocket proxy.
 *
 * Manages two independent pipelines (local provider audio + remote patient audio),
 * each with: MediaStreamTrack → AudioContext resampling → WebSocket → backend → OpenAI.
 * Both pipelines stream all audio continuously and commit on a periodic timer.
 * Segments are saved to backend atomically and emitted via callback.
 *
 * Usage:
 *   const engine = new TranscriptionEngine(workId, onSegment, { onStatus });
 *   engine.startLocal(localAudioMediaStreamTrack);
 *   engine.startRemote(remoteAudioTrack.mediaStreamTrack);
 *   engine.destroy();
 */
import {
  describeAudioTrack,
  logTelehealthAudioDiagnostic,
} from '@/services/telehealthAudioDiagnostics';

const TARGET_SAMPLE_RATE = 24000;
const COMMIT_INTERVAL_MS = 2000;
const PATIENT_AUDIO_GAIN = 2.0;

const RECONNECT_BASE_MS = 2000;
const RECONNECT_CAP_MS = 30000;
const MAX_RECONNECT_RETRIES = 5;

const NON_RECONNECTABLE_CODES = new Set([4401, 4403, 4404]);

export default class TranscriptionEngine {
  /**
   * @param {number} workId — Work record ID for save endpoint
   * @param {function} onSegment — callback({speaker, text, timestamp, unsaved?})
   * @param {{ onStatus?: function }} callbacks — receives capture connection state
   */
  constructor(workId, onSegment, { onStatus, diagnosticId } = {}) {
    this._workId = workId;
    this._onSegment = onSegment;
    this._onStatus = onStatus;
    this._diagnosticId = diagnosticId;
    this._local = null; // { ws, audioContext, sourceNode, workletNode, gainNode }
    this._remote = null;
    this._destroyed = false;
    this._retryCount = { _local: 0, _remote: 0 };
  }

  /** Start transcribing the provider's local audio track. */
  async startLocal(mediaStreamTrack) {
    if (this._destroyed || !mediaStreamTrack) return;
    this._teardown('_local');
    await this._startPipeline(mediaStreamTrack, 'provider', '_local');
  }

  /** Start transcribing the remote patient audio track. */
  async startRemote(mediaStreamTrack, { applyGain = false } = {}) {
    if (this._destroyed || !mediaStreamTrack) return;
    this._applyRemoteGain = applyGain;
    this._teardown('_remote');
    await this._startPipeline(mediaStreamTrack, 'patient', '_remote');
  }

  /** Stop everything. */
  destroy() {
    this._destroyed = true;
    this._teardown('_local');
    this._teardown('_remote');
  }

  // ─── Internal ─────────────────────────────────────────────────────

  async _startPipeline(mediaStreamTrack, speaker, slot) {
    let ws;
    try {
      this._log('pipeline-connecting', {
        speaker,
        track: describeAudioTrack(mediaStreamTrack),
      });
      this._notifyStatus(speaker, 'connecting');
      ws = this._openWs(speaker, slot);

      await new Promise((resolve, reject) => {
        ws.addEventListener('open', resolve, { once: true });
        ws.addEventListener('error', reject, { once: true });
      });

      const pipeline = await this._buildAudioPipeline(
        mediaStreamTrack,
        ws,
        speaker,
        slot
      );
      this[slot] = { ws, ...pipeline, mediaStreamTrack, speaker };
      this._log('pipeline-active', { speaker });
      this._notifyStatus(speaker, 'active');
    } catch (err) {
      console.warn(`TranscriptionEngine: failed to start (${speaker}):`, err);
      this._log(
        'pipeline-start-failed',
        { speaker, errorName: err?.name },
        { level: 'warning', capture: true }
      );
      if (ws) {
        ws._intentionalClose = true;
        ws.close();
      }
      this._notifyStatus(speaker, 'error');
    }
  }

  _notifyStatus(speaker, status) {
    if (this._onStatus) this._onStatus({ speaker, status });
  }

  _log(message, data, options) {
    logTelehealthAudioDiagnostic(
      message,
      { diagnosticId: this._diagnosticId, ...data },
      options
    );
  }

  _buildWsUrl(speaker) {
    const apiBase = import.meta.env.VITE_API_BASE_URL || '/';
    let url;
    try {
      url = new URL(apiBase, window.location.origin);
    } catch {
      url = new URL(apiBase);
    }
    url.protocol = url.protocol === 'https:' ? 'wss:' : 'ws:';
    url.pathname = `/ws/work/${this._workId}/transcription/${speaker}/`;
    return url.toString();
  }

  _openWs(speaker, slot) {
    const ws = new WebSocket(this._buildWsUrl(speaker));

    ws._hasError = false;

    ws.onmessage = event => {
      const msg = JSON.parse(event.data);
      if (msg.type === 'error') {
        const code = msg.error?.code || '';
        if (code === 'input_audio_buffer_commit_empty') return;
        console.error(`Transcription error (${speaker}):`, msg.error);
        this._log(
          'transcription-service-error',
          { speaker, code },
          { level: 'warning', capture: true }
        );
        this._notifyStatus(speaker, 'error');
        return;
      }
      if (msg.type === 'transcript.segment') {
        const segmentData = { ...msg };
        delete segmentData.type;
        this._onSegment(segmentData);
      }
    };

    ws.onerror = () => {
      this._log('websocket-error', { speaker }, { level: 'warning' });
    };

    ws.onopen = () => {
      this._retryCount[slot] = 0;
      this._log('websocket-open', { speaker });
    };

    ws.onclose = event => {
      if (ws._intentionalClose) return;

      this._log(
        'websocket-closed',
        { speaker, code: event.code, wasClean: event.wasClean },
        { level: 'warning' }
      );

      if (NON_RECONNECTABLE_CODES.has(event.code)) {
        console.warn(
          `TranscriptionEngine: connection rejected (${speaker}), code=${event.code}`
        );
        ws._hasError = true;
        this._log(
          'websocket-rejected',
          { speaker, code: event.code },
          { level: 'warning', capture: true }
        );
        this._notifyStatus(speaker, 'error');
        this._teardown(slot);
        return;
      }

      if (!this._destroyed && !ws._hasError) {
        const state = this[slot];
        if (state && state.ws === ws) {
          const track = state.mediaStreamTrack;
          this._teardown(slot);
          if (track && track.readyState === 'live') {
            const retries = this._retryCount[slot];
            if (retries >= MAX_RECONNECT_RETRIES) {
              this._log(
                'websocket-retries-exhausted',
                { speaker, retries },
                { level: 'warning', capture: true }
              );
              this._notifyStatus(speaker, 'error');
              return;
            }
            const delay = Math.min(
              RECONNECT_BASE_MS * Math.pow(2, retries),
              RECONNECT_CAP_MS
            );
            this._retryCount[slot] = retries + 1;
            this._log('websocket-reconnecting', {
              speaker,
              attempt: retries + 1,
              delay,
            });
            this._notifyStatus(speaker, 'reconnecting');
            setTimeout(() => {
              if (!this._destroyed) {
                this._startPipeline(track, speaker, slot);
              }
            }, delay);
          }
        }
      }
    };

    return ws;
  }

  async _buildAudioPipeline(mediaStreamTrack, ws, speaker, slot) {
    const audioContext = new AudioContext();
    if (audioContext.state === 'suspended') {
      console.warn(
        `[TranscriptionEngine] AudioContext suspended for ${speaker}, resuming…`
      );
      await audioContext.resume();
    }
    console.warn(
      `[TranscriptionEngine] pipeline (${speaker}):`,
      `audioContext.state=${audioContext.state} sampleRate=${audioContext.sampleRate}`,
      `track: readyState=${mediaStreamTrack.readyState} enabled=${mediaStreamTrack.enabled}`,
      `muted=${mediaStreamTrack.muted} label="${mediaStreamTrack.label}"`
    );
    this._log('audio-context-ready', {
      speaker,
      state: audioContext.state,
      sampleRate: audioContext.sampleRate,
      track: describeAudioTrack(mediaStreamTrack),
    });

    const workletUrl = new URL('./transcription-processor.js', import.meta.url);
    await audioContext.audioWorklet.addModule(workletUrl);

    const stream = new MediaStream([mediaStreamTrack]);
    const sourceNode = audioContext.createMediaStreamSource(stream);

    let gainNode = null;
    if (speaker === 'patient' && this._applyRemoteGain) {
      gainNode = audioContext.createGain();
      gainNode.gain.value = PATIENT_AUDIO_GAIN;
    }

    const workletNode = new AudioWorkletNode(
      audioContext,
      'transcription-processor',
      { processorOptions: { targetSampleRate: TARGET_SAMPLE_RATE } }
    );
    // Keep the graph rendered without playing microphone audio back to the user.
    // Some browsers suspend disconnected Web Audio graphs, which would otherwise
    // make the worklet (and consequently the transcription) receive no samples.
    const silentOutput = audioContext.createGain();
    silentOutput.gain.value = 0;

    const msPerQuantum = (128 / audioContext.sampleRate) * 1000;
    const MIN_COMMIT_MS = 150;

    let appendedMs = 0;
    let packetCount = 0;
    let audioByteCount = 0;
    let commitCount = 0;
    let lastTransportLogAt = Date.now();
    const logTransportStats = (event, force = false) => {
      if (!force && Date.now() - lastTransportLogAt < 10000) return;
      this._log('audio-transport', {
        speaker,
        event,
        packetCount,
        audioByteCount,
        commitCount,
      });
      packetCount = 0;
      audioByteCount = 0;
      commitCount = 0;
      lastTransportLogAt = Date.now();
    };
    const commitTimer = setInterval(() => {
      if (ws.readyState === WebSocket.OPEN && appendedMs >= MIN_COMMIT_MS) {
        ws.send(JSON.stringify({ type: 'input_audio_buffer.commit' }));
        commitCount++;
        appendedMs = 0;
        logTransportStats('commit');
      }
    }, COMMIT_INTERVAL_MS);

    workletNode.port.onmessage = e => {
      if (ws.readyState !== WebSocket.OPEN) return;
      ws.send(
        JSON.stringify({
          type: 'input_audio_buffer.append',
          audio: this._toBase64(e.data.pcm16),
        })
      );
      packetCount++;
      audioByteCount += e.data.pcm16.byteLength;
      appendedMs += msPerQuantum;
      logTransportStats('append');
    };

    const onTrackEnded = () => {
      this._log('track-ended', {
        speaker,
        track: describeAudioTrack(mediaStreamTrack),
      });
      clearInterval(commitTimer);
      if (appendedMs >= MIN_COMMIT_MS && ws.readyState === WebSocket.OPEN) {
        ws.send(JSON.stringify({ type: 'input_audio_buffer.commit' }));
        commitCount++;
      }
      logTransportStats('track-ended', true);
      appendedMs = 0;
      setTimeout(() => {
        if (!this._destroyed && this[slot]?.ws === ws) {
          this._teardown(slot);
        }
      }, 5000);
    };
    mediaStreamTrack.addEventListener('ended', onTrackEnded, { once: true });
    if (mediaStreamTrack.readyState === 'ended') {
      onTrackEnded();
    }

    if (gainNode) {
      sourceNode.connect(gainNode);
      gainNode.connect(workletNode);
    } else {
      sourceNode.connect(workletNode);
    }
    workletNode.connect(silentOutput);
    silentOutput.connect(audioContext.destination);

    return {
      audioContext,
      sourceNode,
      workletNode,
      gainNode,
      silentOutput,
      commitTimer,
    };
  }

  _toBase64(buffer) {
    const bytes = new Uint8Array(buffer);
    let bin = '';
    for (let i = 0; i < bytes.byteLength; i++) {
      bin += String.fromCharCode(bytes[i]);
    }
    return btoa(bin);
  }

  _teardown(slot) {
    const state = this[slot];
    if (!state) return;

    if (state.commitTimer) {
      clearInterval(state.commitTimer);
    }
    if (state.gainNode) state.gainNode.disconnect();
    state.silentOutput.disconnect();
    state.workletNode.disconnect();
    state.sourceNode.disconnect();
    state.audioContext.close().catch(() => {});
    if (
      state.ws.readyState === WebSocket.OPEN ||
      state.ws.readyState === WebSocket.CONNECTING
    ) {
      state.ws._intentionalClose = true;
      state.ws.close();
    }

    this[slot] = null;
    this._notifyStatus(state.speaker, 'stopped');
  }
}
